import { useMemo, useState } from 'react';
import {
  CONTEST_DOC_TYPES,
  CONTEST_FORM_SECTIONS,
  contestFieldVisible,
  contestFormComplete,
  contestSectionErrors,
  countWords,
  emptyContestFormData,
  withContestLegacyAliases,
  type ContestFormData,
  type ContestFormField,
} from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { ensureMaturationSteps } from '@/features/concours/ContestMaturationPanel';
import { useToast } from '@/features/ui/toast/useToast';

interface Props {
  contests: { id: string; name: string }[];
  contestId: string;
  onContestIdChange: (id: string) => void;
  onSubmitted: (registrationId: string) => void;
}

export function ContestRegistrationWizard({
  contests,
  contestId,
  onContestIdChange,
  onSubmitted,
}: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [sectionIdx, setSectionIdx] = useState(0);
  const [form, setForm] = useState<ContestFormData>(() => {
    const base = emptyContestFormData();
    if (profile) {
      base.nom_prenom = `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim();
      base.email = profile.email ?? '';
      base.telephone = profile.phone_number ?? '';
      base.whatsapp = profile.phone_number ?? '';
    }
    return base;
  });
  const [loading, setLoading] = useState(false);

  const section = CONTEST_FORM_SECTIONS[sectionIdx]!;
  const totalSections = CONTEST_FORM_SECTIONS.length;

  const filledRequired = useMemo(() => {
    let done = 0;
    let total = 0;
    for (const s of CONTEST_FORM_SECTIONS) {
      for (const f of s.fields) {
        if (f.type === 'info' || !f.required || !contestFieldVisible(f, form)) continue;
        total += 1;
        const raw = form[f.key] ?? '';
        if (f.type === 'checkbox') {
          if (raw === 'true') done += 1;
        } else if (raw.trim()) {
          done += 1;
        }
      }
    }
    return { done, total };
  }, [form]);

  const progressPct = filledRequired.total
    ? Math.round((filledRequired.done / filledRequired.total) * 100)
    : 0;

  function setValue(key: string, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleMulti(key: string, option: string) {
    const current = (form[key] || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const next = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    setValue(key, next.join(','));
  }

  function validateCurrent(): boolean {
    const errors = contestSectionErrors(section, form);
    const first = Object.values(errors)[0];
    if (first) {
      toast.error(first);
      return false;
    }
    return true;
  }

  function goNext() {
    if (!validateCurrent()) return;
    setSectionIdx((i) => Math.min(totalSections - 1, i + 1));
  }

  function goPrev() {
    setSectionIdx((i) => Math.max(0, i - 1));
  }

  function goToSection(idx: number) {
    if (idx > sectionIdx && !validateCurrent()) return;
    setSectionIdx(idx);
  }

  async function submit() {
    if (!profile || !contestId) return;
    for (let i = 0; i < CONTEST_FORM_SECTIONS.length; i++) {
      const errors = contestSectionErrors(CONTEST_FORM_SECTIONS[i]!, form);
      if (Object.keys(errors).length > 0) {
        setSectionIdx(i);
        const first = Object.values(errors)[0];
        toast.error(
          first ||
            `Complétez la rubrique « ${CONTEST_FORM_SECTIONS[i]!.label} » avant d’envoyer.`,
        );
        return;
      }
    }
    if (!contestFormComplete(form)) {
      toast.error('Le formulaire est incomplet.');
      return;
    }

    setLoading(true);
    try {
      const { data, error: err } = await kuunda
        .from('contest_registrations')
        .insert({
          user_id: profile.id,
          contest_id: contestId,
          form_data: withContestLegacyAliases(form),
        })
        .select('id')
        .single();
      if (err) throw err;
      if (data?.id) {
        await ensureMaturationSteps(data.id);
        toast.success('Candidature envoyée. Déposez maintenant vos documents ci-dessous.');
        onSubmitted(data.id);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Envoi impossible');
    } finally {
      setLoading(false);
    }
  }

  function renderField(field: ContestFormField) {
    if (!contestFieldVisible(field, form)) return null;

    if (field.type === 'info') {
      return (
        <div key={field.key} className="panel" style={{ padding: '0.85rem 1rem', margin: 0 }}>
          <strong>{field.label}</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem' }}>
            {CONTEST_DOC_TYPES.map((d) => (
              <li key={d.key}>
                {d.label}
                {d.required ? ' *' : ' (facultatif)'}
              </li>
            ))}
          </ul>
          {field.hint ? (
            <p className="muted" style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
              {field.hint}
            </p>
          ) : null}
        </div>
      );
    }

    const value = form[field.key] ?? '';

    if (field.type === 'checkbox') {
      return (
        <div className="field" key={field.key}>
          <label style={{ display: 'flex', gap: '0.55rem', alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={value === 'true'}
              onChange={(e) => setValue(field.key, e.target.checked ? 'true' : 'false')}
              style={{ marginTop: '0.25rem' }}
            />
            <span>
              {field.label}
              {field.required ? ' *' : ''}
            </span>
          </label>
        </div>
      );
    }

    if (field.type === 'multicheck') {
      const selected = value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return (
        <div className="field" key={field.key}>
          <label>
            {field.label}
            {field.required ? ' *' : ''}
          </label>
          <div style={{ display: 'grid', gap: '0.4rem', marginTop: '0.35rem' }}>
            {(field.options ?? []).map((opt) => (
              <label
                key={opt.value}
                style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggleMulti(field.key, opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div className="field" key={field.key}>
          <label htmlFor={`cf-${field.key}`}>
            {field.label}
            {field.required ? ' *' : ''}
          </label>
          <select
            id={`cf-${field.key}`}
            value={value}
            onChange={(e) => setValue(field.key, e.target.value)}
          >
            <option value="">— Sélectionner —</option>
            {(field.options ?? []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {field.hint ? <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>{field.hint}</p> : null}
        </div>
      );
    }

    if (field.type === 'textarea') {
      const words = countWords(value);
      return (
        <div className="field" key={field.key}>
          <label htmlFor={`cf-${field.key}`}>
            {field.label}
            {field.required ? ' *' : ''}
          </label>
          <textarea
            id={`cf-${field.key}`}
            rows={field.rows ?? 4}
            value={value}
            placeholder={field.placeholder}
            onChange={(e) => setValue(field.key, e.target.value)}
          />
          <div className="muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
            {field.hint ? `${field.hint} · ` : null}
            {field.minWords || field.maxWords ? `${words} mot${words > 1 ? 's' : ''}` : null}
          </div>
        </div>
      );
    }

    return (
      <div className="field" key={field.key}>
        <label htmlFor={`cf-${field.key}`}>
          {field.label}
          {field.required ? ' *' : ''}
        </label>
        <input
          id={`cf-${field.key}`}
          type={field.type}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => setValue(field.key, e.target.value)}
        />
        {field.hint ? <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>{field.hint}</p> : null}
      </div>
    );
  }

  const isLast = sectionIdx === totalSections - 1;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <p className="muted" style={{ margin: 0 }}>
            Rubrique {section.number} / {totalSections}
          </p>
          <strong>
            {filledRequired.done}/{filledRequired.total} champs requis · {progressPct} %
          </strong>
        </div>
        <div style={{ flex: '1 1 12rem', maxWidth: '18rem' }}>
          <div
            style={{
              height: 8,
              borderRadius: 999,
              background: 'rgba(15,40,70,0.08)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                height: '100%',
                background: 'var(--brand)',
                transition: 'width 0.2s ease',
              }}
            />
          </div>
        </div>
      </div>

      <div className="field" style={{ marginBottom: '1rem' }}>
        <label htmlFor="contest-select">Concours</label>
        <select
          id="contest-select"
          value={contestId}
          onChange={(e) => onContestIdChange(e.target.value)}
        >
          {contests.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="u-steps" role="tablist" aria-label="Rubriques du formulaire">
        {CONTEST_FORM_SECTIONS.map((s, idx) => {
          const sectionDone = Object.keys(contestSectionErrors(s, form)).length === 0;
          return (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={sectionIdx === idx}
              className={`u-step ${sectionIdx === idx ? 'is-active' : ''} ${sectionDone ? 'is-done' : ''}`}
              title={`${s.number}. ${s.label}`}
              onClick={() => goToSection(idx)}
            >
              {s.number}
            </button>
          );
        })}
      </div>

      <section>
        <h3 className="u-section-title" style={{ marginTop: 0 }}>
          {section.number}. {section.label}
        </h3>
        {section.description ? <p className="muted">{section.description}</p> : null}

        <div className="grid-2">
          {section.fields.map((field) => {
            if (!contestFieldVisible(field, form)) return null;
            const fullWidth =
              field.type === 'textarea' ||
              field.type === 'info' ||
              field.type === 'multicheck' ||
              field.type === 'checkbox' ||
              field.key === 'adresse' ||
              field.key === 'experience_professionnelle';
            return (
              <div key={field.key} style={fullWidth ? { gridColumn: '1 / -1' } : undefined}>
                {renderField(field)}
              </div>
            );
          })}
        </div>
      </section>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1.25rem' }}>
        <button type="button" className="btn btn-ghost" disabled={sectionIdx === 0} onClick={goPrev}>
          Précédent
        </button>
        {!isLast ? (
          <button type="button" className="btn btn-primary" onClick={goNext}>
            Suivant
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading || !contestId}
            onClick={() => void submit()}
          >
            {loading ? 'Envoi…' : 'Soumettre la candidature'}
          </button>
        )}
      </div>
    </div>
  );
}
